import { useCallback } from 'react';
import { useParams } from 'react-router';
import { EmployeeService } from 'src/api/EmployeeService';
import type { Pattern } from 'src/api/EmployeeService';
import { invalidateEmployee, invalidateEmployees } from '@stores/employeeStore';

type SaveEmployeePattern = (
    employeeId: string,
    patternCase: string,
    pattern: Pattern,
    patternId?: string,
) => Promise<Pattern | undefined>;

const useSaveEmployeePattern = (): {
    saveEmployeePattern: SaveEmployeePattern;
} => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const saveEmployeePattern = useCallback<SaveEmployeePattern>(
        async (employeeId, patternCase, pattern, patternId) => {
            if (!departmentId) {
                throw new Error(
                    'useSaveEmployeePattern requires a departmentId route param',
                );
            }

            const savedPattern = patternId
                ? await EmployeeService.updateEmployeePattern(
                      departmentId,
                      employeeId,
                      patternId,
                      patternCase,
                      pattern,
                  )
                : await EmployeeService.createEmployeePattern(
                      departmentId,
                      employeeId,
                      patternCase,
                      pattern,
                  );

            if (!savedPattern) {
                return undefined;
            }

            invalidateEmployee(departmentId, employeeId);
            invalidateEmployees(departmentId);

            return savedPattern;
        },
        [departmentId],
    );

    return { saveEmployeePattern };
};

export default useSaveEmployeePattern;
